<template>
  <div v-if="getCommentsLoading" class="tw:flex tw:flex-col tw:gap-4 tw:w-full tw:py-4">
    <div v-for="i in 5" :key="i" class="tw:flex tw:gap-4 tw:items-start tw:border-b tw:border-(--divider-color) tw:pb-4">
      <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-(--divider-color) tw:animate-pulse tw:shrink-0"></div>
      <div class="tw:flex tw:flex-col tw:gap-2 tw:flex-1">
        <TextPlay skeleton size="sm" text="placeholder name" />
        <TextPlay skeleton size="sm" text="placeholder comment text that is a bit longer" />
      </div>
      <div class="tw:w-[150px] tw:shrink-0">
        <TextPlay skeleton size="sm" text="placeholder lesson" />
      </div>
    </div>
  </div>

  <div v-else>
    <div v-if="selectedCommentIds.length > 0" class="tw:flex tw:gap-2 tw:items-center">
      <TextPlay color="var(--text-color)" weight="semibold" size="sm" :text="$t('comments_mod.you_selected', { count: selectedCommentIds.length })" />
      <ButtonPlayV2 data-testid="approve" variant="filled" color="var(--feedback-success-500)" :text="$t('comments.to_approve')" @click="handleSelectedList('published')" />
      <ButtonPlayV2 data-testid="reprove" variant="filled" color="var(--feedback-error-500)" :text="$t('comments.to_reprove')" @click="handleSelectedList('reproved')" />
    </div>

    <table v-if="comments.total > 0" class="tw:table-fixed tw:w-full">
      <thead class="tw:border-b tw:border-(--divider-color)">
        <tr>
          <th class="tw:py-4 tw:pe-2 tw:text-start tw:align-middle tw:w-10">
            <CheckboxPlay
              id="check-all"
              name="check-all"
              :model-value="allSelected"
              @update:model-value="handleCheckAll"
            />
          </th>
          <th class="tw:py-4 tw:pe-4 tw:text-start">
            <TextPlay
              size="sm"
              color="var(--fontcolor)"
              weight="semibold"
              :text="$t('comments_mod.created_by')"
            />
          </th>
          <th class="tw:py-4 tw:text-start tw:w-[300px]">
            <TextPlay
              size="sm"
              color="var(--fontcolor)"
              weight="semibold"
              :text="$t('comments_mod.lesson')"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="c in comments.data"
          :key="c.id"
          class="tw:border-b tw:border-(--divider-color)"
        >
          <td class="tw:py-6 tw:pe-2 tw:align-top">
            <CheckboxPlay
              :id="`comment-${c.id}`"
              :name="`comment-${c.id}`"
              :model-value="selectedCommentIds"
              :checked-value="c.id"
              @update:model-value="handleCommentCheck"
            />
          </td>
          <td class="tw:py-4 tw:pe-4">
            <WatchLessonCommentsItem
              :id="c.id"
              :key="c.id"
              :label="c.comment"
              :autor="c.member"
              :created-at="c.created_at"
              :updated-at="c.updated_at"
              :deleted-at="c.deleted_at"
              :is-mine="c.is_mine"
              :status="c.status"
              :liked="c.liked"
              :likes-count="c.likes_count"
              :nodes="c.replies"
              :depth="0"
              :course-id="c.lesson.course_id"
              :module-id="c.lesson.module_id"
              :lesson-id="c.lesson.id"
            />
          </td>
          <td class="tw:py-4 tw:align-top">
            <router-link class="tw:flex tw:gap-2" :to="{ name: 'watch-lesson', params: { courseId: c.lesson.course_id, moduleId: c.lesson.module_id, lessonId: c.lesson.id } }">
              <IconGoogle color="var(--maincolor)" size="14px" name="open_in_new" />
              <TextPlay
                decoration="underline"
                color="var(--maincolor)"
                size="sm"
                :text="c.lesson.title"
              />
            </router-link>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else>
      <TextPlay size="sm" color="var(--text-color)" :text="$t('comments_mod.no_comments')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import WatchLessonCommentsItem from "@/components/atomic/molecules/LessonComment/LessonComment.vue";
import { useWatchStore } from "@/store/watch.store";

defineProps<{ comments: Record<string, unknown> & { total?: number; data?: Array<Record<string, unknown>> } }>()

const watchStore = useWatchStore();
const selectedCommentIds = ref<string[]>([])
const allSelected = ref(false)

async function handleCheckAll(checked: boolean) {
	if (checked) {
		selectedCommentIds.value = await watchStore.actionLoadFilteredCommentIds()
		allSelected.value = true
	} else {
		selectedCommentIds.value = []
		allSelected.value = false
	}
}

function handleCommentCheck(newValue: string[]) {
	selectedCommentIds.value = newValue
	if (allSelected.value) {
		allSelected.value = false
	}
}

const getCurrentModFilters = computed(() => watchStore.getCurrentModFilters);

watch([() => getCurrentModFilters.value.searchString, () => getCurrentModFilters.value.status], () => {
	selectedCommentIds.value = []
	allSelected.value = false
});

async function handleSelectedList(status: string){
	await watchStore.actionHandleSelectedList({ selectedIds: selectedCommentIds.value, status: status })
	selectedCommentIds.value = []
	allSelected.value = false
}

const getCommentsLoading = computed(() => watchStore.getLoading.comments);
</script>
